import { useNavigate } from "react-router-dom";
import { LogIn, LogOut, LayoutDashboard, ChevronDown, Menu, X } from "lucide-react";
import { Button } from "@shared/components/ui/button";
import { PlatformLogo } from "@shared/components/common/PlatformLogo";
import { AdminNotifications } from "@shared/components/common/AdminNotifications";
import { UserNotifications } from "@shared/components/common/UserNotifications";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@shared/components/ui/navigation-menu";
import { supabase } from "@lib/supabase/client";
import { useState, useEffect } from "react";
import type { User } from '@supabase/supabase-js';
import { cn } from "@shared/utils/utils";

interface MainHeaderProps {
  user: User | null;
  isAdmin: boolean;
  onAuthAction: () => void;
  transparent?: boolean;
}

const MainHeader = ({ user, isAdmin, onAuthAction, transparent = false }: MainHeaderProps) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const isTransparent = transparent && !isScrolled && !isHovered;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDashboardClick = async () => {
    if (!user) return;
    
    // Check org membership first
    const { data: orgMembership } = await supabase
      .from("org_specialists")
      .select("id")
      .eq("specialist_email", (user.email ?? '').toLowerCase())
      .eq("is_active", true)
      .maybeSingle();

    if (orgMembership) {
      navigate('/dashboard');
      return;
    }

    const { data: hasMonthly } = await supabase.rpc('user_has_product_access', { 
      _user_id: user.id, 
      _product_key: 'specialist-dashboard-monthly' 
    });
    const { data: hasYearly } = await supabase.rpc('user_has_product_access', { 
      _user_id: user.id, 
      _product_key: 'specialist-dashboard-yearly' 
    });
    const { data: isAllowedMonthly } = await supabase.rpc('check_allowed_email', { 
      p_email: user.email ?? '', 
      p_product_key: 'specialist-dashboard-monthly' 
    });
    const { data: isAllowedYearly } = await supabase.rpc('check_allowed_email', { 
      p_email: user.email ?? '', 
      p_product_key: 'specialist-dashboard-yearly' 
    });
    
    const hasAccess = Boolean(hasMonthly) || Boolean(hasYearly) || Boolean(isAllowedMonthly) || Boolean(isAllowedYearly) || isAdmin;
    
    if (hasAccess) {
      navigate('/dashboard');
    } else {
      navigate('/subscribe');
    }
  };

  const navItems = [
    {
      label: "الرئيسية",
      hasDropdown: false,
      href: "/"
    },
    {
      label: "الحلول",
      hasDropdown: false,
      href: "/programs"
    },
    {
      label: "من نحن",
      hasDropdown: true,
      items: [
        { label: "نبذة عنا", href: "/about", description: "تعرف على DietiTech" },
        { label: "آراء العملاء", href: "/testimonials", description: "ماذا يقول عملاؤنا" },
      ]
    },
    {
      label: "الدعم والتواصل",
      hasDropdown: true,
      items: [
        { label: "تواصل معنا", href: "/contact", description: "نحن هنا لمساعدتك" },
        { label: "الأسئلة الشائعة", href: "/faq", description: "إجابات سريعة" },
      ]
    },
  ];

  return (
    <header 
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className={cn(
        "top-0 z-50 w-full transition-all duration-300",
        transparent ? "fixed" : "sticky",
        isTransparent 
          ? "bg-transparent border-b border-transparent shadow-none" 
          : "bg-background border-b border-border/40 shadow-sm"
      )}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Right Side - Logo & Name */}
          <div 
            className="flex items-center gap-1 md:gap-1.5 cursor-pointer"
            onClick={() => navigate('/')}
          >
            <div className="flex h-12 md:h-14 w-[60px] md:w-[70px] flex-shrink-0 items-center justify-end overflow-hidden">
              <PlatformLogo className="block h-full w-auto max-w-full object-contain" />
            </div>
            <div className="text-right leading-tight hidden sm:block">
              <h1 className={cn("text-[18px] md:text-[22px] font-bold leading-none", isTransparent ? "text-white" : "text-foreground")}>DietiTech</h1>
              <p className={cn("text-[6px] md:text-[7px] mt-0.5", isTransparent ? "text-white/70" : "text-muted-foreground")}>Dietitian Technology Solutions</p>
              <p className={cn("text-[6px] md:text-[7px] mt-0.5", isTransparent ? "text-white/70" : "text-muted-foreground")}>حلول تقنية لأخصائي التغذية</p>
            </div>
          </div>

          {/* Center - Navigation Menu (Desktop) */}
          <div className="hidden lg:flex items-center">
            <NavigationMenu dir="rtl" className="mx-auto">
              <NavigationMenuList className="gap-1">
                {navItems.map((item, index) => (
                  <NavigationMenuItem key={index}>
                    {item.hasDropdown ? (
                      <>
                        <NavigationMenuTrigger className={cn("bg-transparent hover:bg-muted/50 text-sm font-medium transition-all duration-200 hover:font-semibold hover:scale-105", isTransparent && "text-white hover:bg-white/10 hover:text-white")}>
                          {item.label}
                        </NavigationMenuTrigger>
                        <NavigationMenuContent>
                        <ul className="grid w-[300px] gap-2 p-4 bg-popover">
                            {item.items?.map((subItem, subIndex) => (
                              <li key={subIndex}>
                                <NavigationMenuLink asChild>
                                  <a
                                    href={subItem.href}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      if (subItem.href === '/dashboard') {
                                        handleDashboardClick();
                                      } else {
                                        navigate(subItem.href);
                                      }
                                    }}
                                    className="block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-muted hover:text-foreground focus:bg-muted focus:text-foreground"
                                  >
                                    <div className="text-sm font-medium leading-none mb-1">{subItem.label}</div>
                                    <p className="text-xs leading-snug text-muted-foreground">
                                      {subItem.description}
                                    </p>
                                  </a>
                                </NavigationMenuLink>
                              </li>
                            ))}
                          </ul>
                        </NavigationMenuContent>
                      </>
                    ) : (
                      <NavigationMenuLink asChild>
                        <a
                          href={item.href}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(item.href || '/');
                          }}
                          className={cn("inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium transition-all duration-200 hover:bg-muted/50 hover:text-foreground hover:font-semibold hover:scale-105 focus:bg-muted focus:text-foreground focus:outline-none", isTransparent && "text-white hover:bg-white/10 hover:text-white")}
                        >
                          {item.label}
                        </a>
                      </NavigationMenuLink>
                    )}
                  </NavigationMenuItem>
                ))}
              </NavigationMenuList>
            </NavigationMenu>
          </div>

          {/* Left Side - Buttons */}
          <div className="flex gap-2 md:gap-3 items-center">
            {user && !isAdmin && <UserNotifications />}
            {isAdmin && <AdminNotifications />}
            
            {user && (
              <Button
                variant="default"
                onClick={handleDashboardClick}
                className="gap-2 px-3 md:px-5 py-2 text-xs md:text-sm h-9 md:h-10"
              >
                <LayoutDashboard size={16} />
                <span className="hidden sm:inline">لوحة التحكم</span>
              </Button>
            )}
            
            <Button
              variant="outline"
              onClick={onAuthAction}
              className={cn("gap-2 px-3 md:px-5 py-2 text-xs md:text-sm h-9 md:h-10", isTransparent && "border-white/50 text-white bg-transparent hover:bg-white/10 hover:text-white")}
            >
              {user ? (
                <>
                  <LogOut size={16} />
                  <span className="hidden sm:inline">خروج</span>
                </>
              ) : (
                <>
                  <LogIn size={16} />
                  <span className="hidden sm:inline">تسجيل الدخول</span>
                </>
              )}
            </Button>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden pb-4 border-t border-border/40 pt-4">
            <nav className="flex flex-col gap-2">
              {navItems.map((item, index) => (
                <div key={index}>
                  {item.hasDropdown ? (
                    <details className="group">
                      <summary className="flex items-center justify-between cursor-pointer px-4 py-3 rounded-lg hover:bg-muted/50 font-medium text-sm">
                        {item.label}
                        <ChevronDown size={16} className="transition-transform group-open:rotate-180" />
                      </summary>
                      <div className="mr-4 mt-1 flex flex-col gap-1">
                        {item.items?.map((subItem, subIndex) => (
                          <a
                            key={subIndex}
                            href={subItem.href}
                            onClick={(e) => {
                              e.preventDefault();
                              if (subItem.href === '/dashboard') {
                                handleDashboardClick();
                              } else {
                                navigate(subItem.href);
                              }
                              setMobileMenuOpen(false);
                            }}
                            className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/30 rounded-lg"
                          >
                            {subItem.label}
                          </a>
                        ))}
                      </div>
                    </details>
                  ) : (
                    <a
                      href={item.href}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(item.href || '/');
                        setMobileMenuOpen(false);
                      }}
                      className="flex items-center px-4 py-3 rounded-lg hover:bg-muted/50 font-medium text-sm"
                    >
                      {item.label}
                    </a>
                  )}
                </div>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default MainHeader;
